import { ref, readonly } from 'vue'
import i18n from '@/i18n'
import api from '@/lib/axios'
import { useAuth } from '@/hooks/useAuth'

const SUPPORTED = Object.freeze(['es', 'en', 'el'])

// Module-level reactive ref shared across components.
// Initialized from i18n's current locale (set at boot) so first reads are accurate.
const currentLocale = ref(
  SUPPORTED.includes(i18n.global.locale.value) ? i18n.global.locale.value : 'es'
)

/**
 * Apply a locale change locally (i18n + axios header + module ref).
 * Safe to call from any context — does NOT require Vue setup context.
 *
 * @param {string} next - One of 'es' | 'en' | 'el'.
 */
export function applyLocale(next) {
  if (!SUPPORTED.includes(next)) {
    throw new Error(`Unsupported locale: ${next}`)
  }
  currentLocale.value = next
  i18n.global.locale.value = next
  api.defaults.headers.common['Accept-Language'] = next
}

/**
 * Composable for components/setup contexts that want to react to locale changes.
 * Returns the shared module-level state plus a `setLocale` that PATCHes the user.
 *
 * @throws {Error} when `setLocale` is called with persist=true and no authenticated user.
 * @throws {Error} when the PATCH /api/v1/users/{id} request fails (caller should handle).
 */
export function useLocalePreference() {
  const { user } = useAuth()

  /**
   * Update the user's UI locale.
   *
   * @param {string} next - One of 'es' | 'en' | 'el'.
   * @param {{ persist?: boolean }} [options]
   *   persist: if true (default), PATCH /api/v1/users/{id}.
   *            if false, only mutate local state (used at boot).
   */
  const setLocale = async (next, { persist = true } = {}) => {
    if (!SUPPORTED.includes(next)) {
      throw new Error(`Unsupported locale: ${next}`)
    }

    if (persist) {
      const userId = user.value?.id
      if (!userId) {
        throw new Error('No authenticated user; cannot persist locale')
      }
      await api.patch(`/api/v1/users/${userId}`, { locale: next })
    }

    applyLocale(next)
  }

  return {
    currentLocale: readonly(currentLocale),
    supportedLocales: SUPPORTED,
    setLocale,
  }
}
